import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  UserPlus,
  Menu,
  FileText,
  Settings,
  User,
  Users,
  Home,
  Building2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Calendar,
  CalendarDays,
  List,
  Shield,
  ShieldCheck,
  Building,
  TrendingUp,
  ShoppingBag,
  Briefcase,
  Smartphone,
  Image,
  AppWindow,
  UserCog,
  HelpCircle,
  Bell,
  KeyRound,
  Tag,
  Clock,
  Landmark,
  MapPin,
  LifeBuoy,
  Inbox,
} from "lucide-react";
import { useBusinessContext } from "../context/BusinessContext";
import { useUserPermissions } from "../hooks/useUserPermissions";
import { useMenuBuilder, MenuItemData } from "../hooks/useMenuBuilder";
import { Tooltip, TooltipProvider } from "./ui/Tooltip";
import { HoverCard } from "./ui/HoverCard";
import { cn } from "../lib/cn";

// ─────────────────────────────────────────────────────────────────────────────
// Phase 5 Tailwind pilot (SAM-179): the 228px labeled rail from the design,
// built on tokens.css. Replaces the plain-CSS SideMenu; overlays (collapsed
// flyouts) ride on Radix via ui/Tooltip + ui/HoverCard.

const iconMap: Record<string, React.ComponentType<React.ComponentProps<typeof LayoutDashboard>>> = {
  LayoutDashboard, UserPlus, Menu, FileText, Settings, User, Users, Home, Building2,
  CreditCard, Calendar, CalendarDays, List, Shield, ShieldCheck, Building, TrendingUp,
  ShoppingBag, Briefcase, Smartphone, Image, AppWindow, UserCog, HelpCircle, Bell,
  KeyRound, Tag, Clock, Landmark, MapPin, LifeBuoy, Inbox,
};

// The left-border color is applied per-state (itemActive/itemIdle) because
// stacking border-l-transparent + border-l-tenant would leave the winner to
// stylesheet order.
const itemBase =
  "flex cursor-pointer items-center gap-[10px] border-0 border-l-[3px] border-solid px-md py-[10px] " +
  "text-sm font-medium text-ink-500 transition-colors hover:bg-surface-2 hover:text-ink-900";
const itemIdle = "border-l-transparent";
const itemActive = "border-l-tenant bg-line-subtle font-semibold text-tenant hover:bg-line-subtle hover:text-tenant";
const subBase =
  "flex cursor-pointer items-center gap-[10px] border-0 border-l-[3px] border-solid py-[9px] pl-[44px] pr-md " +
  "text-[13px] font-medium text-ink-500 transition-colors hover:bg-surface-2 hover:text-ink-900";

interface SidebarProps {
  /** 'drawer' renders always-expanded (mobile menu) and hides the collapse toggle */
  variant?: "desktop" | "drawer";
  /** Called after navigating from a menu item — used to close the mobile drawer */
  onNavigate?: () => void;
}

/** Presentational rail — hook-free so tests can drive it directly. */
export interface SidebarViewProps {
  menuData: MenuItemData[];
  activeItemId: string;
  collapsed: boolean;
  variant: "desktop" | "drawer";
  isLoading?: boolean;
  onNavigate: (itemId: string) => void;
  onToggleCollapse: () => void;
}

export const SidebarView: React.FC<SidebarViewProps> = ({
  menuData,
  activeItemId,
  collapsed,
  variant,
  isLoading = false,
  onNavigate,
  onToggleCollapse,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [flyoutId, setFlyoutId] = useState<string | null>(null);

  // SAM-944: in the phone menu the rail fills the panel (no side border) and its rows are 48 px targets.
  const drawer = variant === "drawer";
  const railClass = drawer
    ? "flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white"
    : cn(
        "flex shrink-0 flex-col overflow-hidden border-0 border-r border-solid border-line bg-white transition-[width] duration-200",
        collapsed ? "w-16" : "w-[228px]",
      );
  const rowSize = drawer ? "min-h-12 text-[15px]" : "";

  if (isLoading) {
    return (
      <nav className={railClass} aria-label="Main navigation">
        <div className="px-md py-[14px] text-[13px] font-medium text-ink-500">Loading…</div>
      </nav>
    );
  }

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const navigateAndCloseFlyout = (itemId: string) => {
    onNavigate(itemId);
    setFlyoutId(null);
  };

  const renderFlyoutItems = (items: MenuItemData[], depth = 0): React.ReactNode =>
    items.map((sub) => {
      const SubIcon = iconMap[sub.iconName];
      const hasNested = !!sub.subItems?.length;
      if (hasNested) {
        return (
          <React.Fragment key={sub.itemId}>
            <div className={cn("px-sm py-[6px] text-[11px] font-semibold uppercase tracking-wide text-ink-400", depth > 0 && "mt-[4px]")}>
              {sub.title}
            </div>
            {renderFlyoutItems(sub.subItems!, depth + 1)}
          </React.Fragment>
        );
      }
      return (
        <div
          key={sub.itemId}
          className={cn(
            "flex cursor-pointer items-center gap-[10px] rounded-8 px-sm py-[9px] text-sm font-medium text-ink-900 hover:bg-line-subtle",
            depth > 0 && "pl-[20px]",
            activeItemId === sub.itemId && "bg-line-subtle",
          )}
          role="button"
          tabIndex={0}
          aria-label={sub.title}
          aria-current={activeItemId === sub.itemId ? "page" : undefined}
          onClick={() => navigateAndCloseFlyout(sub.itemId)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") (e.currentTarget as HTMLDivElement).click();
          }}
        >
          {SubIcon && <SubIcon size={16} strokeWidth={1.8} />}
          <span>{sub.title}</span>
        </div>
      );
    });

  return (
    <TooltipProvider>
      <nav className={railClass} aria-label="Main navigation">
        <div className="flex-1 overflow-y-auto py-sm scrollbar-none">
          {menuData.map((item) => {
            const IconComponent = iconMap[item.iconName];
            const isActive = activeItemId === item.itemId;
            const hasSubItems = !!item.subItems?.length;
            const isExpanded = expandedItems.has(item.itemId);

            const row = (
              <div
                className={cn(
                  itemBase,
                  rowSize,
                  isActive ? itemActive : itemIdle,
                  collapsed && "justify-center gap-0 px-0 py-[11px]",
                )}
                role="button"
                tabIndex={0}
                aria-label={item.title}
                aria-current={isActive ? "page" : undefined}
                aria-expanded={hasSubItems && !collapsed ? isExpanded : undefined}
                onClick={() => {
                  if (collapsed) {
                    // Collapsed: a group's first child is the natural target.
                    navigateAndCloseFlyout(hasSubItems ? item.subItems![0].itemId : item.itemId);
                  } else if (hasSubItems) {
                    toggleExpanded(item.itemId);
                  } else {
                    onNavigate(item.itemId);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") (e.currentTarget as HTMLDivElement).click();
                }}
              >
                {IconComponent && <IconComponent size={collapsed ? 20 : 18} strokeWidth={1.8} />}
                {!collapsed && <span className="flex-1">{item.title}</span>}
                {hasSubItems && !collapsed && (
                  <ChevronRight
                    className={cn("ml-auto h-[15px] w-[15px] transition-transform duration-200", isExpanded && "rotate-90")}
                  />
                )}
              </div>
            );

            return (
              <React.Fragment key={item.itemId}>
                {collapsed && hasSubItems ? (
                  <HoverCard
                    open={flyoutId === item.itemId}
                    onOpenChange={(o) => setFlyoutId(o ? item.itemId : null)}
                    content={
                      <>
                        <div className="px-sm pb-[2px] pt-[4px] text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                          {item.title}
                        </div>
                        {renderFlyoutItems(item.subItems!)}
                      </>
                    }
                  >
                    {row}
                  </HoverCard>
                ) : collapsed ? (
                  <Tooltip content={item.title}>{row}</Tooltip>
                ) : (
                  row
                )}

                {hasSubItems && isExpanded && !collapsed &&
                  item.subItems!.map((sub) => {
                    const SubIcon = iconMap[sub.iconName];
                    const isSubActive = activeItemId === sub.itemId;
                    const hasNested = !!sub.subItems?.length;
                    const isSubExpanded = expandedItems.has(sub.itemId);

                    return (
                      <React.Fragment key={sub.itemId}>
                        <div
                          className={cn(subBase, rowSize, isSubActive ? itemActive : itemIdle)}
                          role="button"
                          tabIndex={0}
                          aria-label={sub.title}
                          aria-current={isSubActive ? "page" : undefined}
                          aria-expanded={hasNested ? isSubExpanded : undefined}
                          onClick={() => (hasNested ? toggleExpanded(sub.itemId) : onNavigate(sub.itemId))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") (e.currentTarget as HTMLDivElement).click();
                          }}
                        >
                          {SubIcon && <SubIcon size={16} strokeWidth={1.8} />}
                          <span className="flex-1">{sub.title}</span>
                          {hasNested && (
                            <ChevronRight
                              className={cn("ml-auto h-[15px] w-[15px] transition-transform duration-200", isSubExpanded && "rotate-90")}
                            />
                          )}
                        </div>

                        {hasNested && isSubExpanded &&
                          sub.subItems!.map((nested) => {
                            const NestedIcon = iconMap[nested.iconName];
                            return (
                              <div
                                key={nested.itemId}
                                className={cn(subBase, rowSize, "pl-[56px]", activeItemId === nested.itemId ? itemActive : itemIdle)}
                                role="button"
                                tabIndex={0}
                                aria-label={nested.title}
                                aria-current={activeItemId === nested.itemId ? "page" : undefined}
                                onClick={() => onNavigate(nested.itemId)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") (e.currentTarget as HTMLDivElement).click();
                                }}
                              >
                                {NestedIcon && <NestedIcon size={15} strokeWidth={1.8} />}
                                <span>{nested.title}</span>
                              </div>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </div>

        {variant !== "drawer" && (
          <div
            className={cn(
              "flex cursor-pointer items-center border-0 border-t border-solid border-line px-md py-[14px] text-[13px] font-medium text-ink-700 hover:bg-surface-2",
              collapsed ? "justify-center" : "justify-between",
            )}
            role="button"
            tabIndex={0}
            aria-label={collapsed ? "Show bar" : "Hide bar"}
            onClick={onToggleCollapse}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onToggleCollapse();
            }}
          >
            {!collapsed && <span>Hide bar</span>}
            <ChevronLeft size={16} strokeWidth={2} className={cn(collapsed && "rotate-180")} />
          </div>
        )}
      </nav>
    </TooltipProvider>
  );
};

/** App-wired sidebar: permissions → menu, router-driven active item, context collapse. */
/**
 * Which menu entry is current. An entry whose id carries a query (`#users?tab=estate-requests`) matches its path
 * when the URL has the same value for each of the entry's params (other params, like the search `q`, are
 * ignored). It is tried first, so it wins over the plain entry for the same path (`#users`).
 */
export const findActiveItemId = (menu: MenuItemData[], pathname: string, search: string): string => {
  const flat: MenuItemData[] = [];
  const walk = (items: MenuItemData[]) => items.forEach((i) => { flat.push(i); if (i.subItems) walk(i.subItems); });
  walk(menu);
  const params = new URLSearchParams(search);
  const matchesQuery = (itemId: string): boolean => {
    const [path, query] = itemId.replace('#', '/').split('?');
    if (pathname !== path) return false;
    return [...new URLSearchParams(query).entries()].every(([k, v]) => params.get(k) === v);
  };
  const withQuery = flat.find((i) => i.itemId.includes('?') && matchesQuery(i.itemId));
  if (withQuery) return withQuery.itemId;
  // Deepest entries first, so a sub-entry beats the section that contains it.
  const plain = flat.filter((i) => !i.itemId.includes('?')).reverse();
  for (const i of plain) {
    const target = i.itemId.replace('#', '/');
    if (pathname === target || pathname.startsWith(`${target}/`)) return i.itemId;
  }
  return '';
};

export const Sidebar: React.FC<SidebarProps> = ({ variant = "desktop", onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarCollapsed: ctxCollapsed, setSidebarCollapsed } = useBusinessContext();
  // The mobile drawer always shows the expanded menu regardless of the
  // desktop collapse preference (SAM-169).
  const collapsed = variant === "drawer" ? false : ctxCollapsed;
  const permissions = useUserPermissions();
  const menuData = useMenuBuilder(permissions);

  const activeItemId = findActiveItemId(menuData, location.pathname, location.search);

  return (
    <SidebarView
      menuData={menuData}
      activeItemId={activeItemId}
      collapsed={collapsed}
      variant={variant}
      isLoading={permissions.isLoading}
      onNavigate={(itemId) => {
        navigate(itemId.replace("#", ""));
        onNavigate?.();
      }}
      onToggleCollapse={() => setSidebarCollapsed(!collapsed)}
    />
  );
};
